/**
 * CORE-BL — Guided Onboarding & Product Education · experience identity
 *
 * Constitutional authority:
 *   `docs/constitution/07 - Feature Library/02 - Core/
 *    C-30 Onboarding, Product Education & Beta Programme/
 *    CORE-BL - Guided onboarding & product education.md`
 *
 * One role vocabulary
 * -------------------
 * `AppRole` is the **generated `app_role` enum**, not a hand-written union.
 * A previous revision of this file declared its own copy, which defeated the
 * drift protection it claimed: a role added to the database would have left
 * onboarding compiling happily while teaching that role nothing. The
 * generated type is the only source that cannot silently disagree with the
 * column, so adding a role now breaks this file until somebody decides what
 * it is taught.
 *
 * One capability vocabulary
 * -------------------------
 * `CapabilityKey` is derived from the app's existing permission surface, and
 * narrowed to its boolean members. `requiredCapability: 'canCreateTikets'`
 * used to compile and silently delete a lesson; it is now a type error.
 * **This remains presentation, not authorization** — the keys decide what is
 * TAUGHT, never what is ALLOWED, and SP-03 stays the authority server-side.
 *
 * What a persisted experience key MEANS
 * -------------------------------------
 * **One key = one exact instruction contract**, not "some people who share a
 * broad role family". If two people can receive a different ordered sequence,
 * their durable progress must not share a completion identity — otherwise an
 * advisor who finished their walkthrough would silently mark a dispatcher
 * complete for lessons the dispatcher never saw.
 *
 * So a key is split when the SEQUENCE differs, and shared when it is
 * genuinely identical. Wording changes do not earn a new key; audience and
 * sequence changes do.
 */

import type { Database } from '@/integrations/supabase/types';
// Type-only: the permission surface stays owned where it is, and nothing
// here imports its runtime.
import type { usePermissions } from '@/hooks/usePermissions';

/** The canonical role vocabulary — the generated `app_role` enum. */
export type AppRole = Database['public']['Enums']['app_role'];

/**
 * The roles, enumerated at runtime.
 *
 * A handwritten list mirroring a generated enum is a drift risk, so it is
 * pinned in BOTH directions below. `satisfies` alone was not enough: it
 * rejects an entry that is not a real role, and says nothing about a real role
 * that is missing. Missing was the direction that mattered — a role added to
 * the database would have left this list short, `isCanonicalAppRole` would
 * have rejected that person's real membership, and they would have been told
 * their role was unrecognised.
 */
export const CANONICAL_APP_ROLES = Object.freeze([
  'MANAGER',
  'SERVICE_WRITER',
  'TECHNICIAN',
  'DISPATCHER',
  'PARTS',
  'AUDITOR',
  'ORG_ADMIN',
  'OWNER',
  'REGION_MANAGER',
  'DISTRICT_MANAGER',
] as const) satisfies ReadonlyArray<AppRole>;

/**
 * Roles the DATABASE has and the generated types do not — yet.
 *
 * `types.ts` is generated from a hosted project, so it lags a migration that
 * has landed here but not there. `STAFF` is exactly that case: multi-role
 * added it to `app_role` so that somebody's ACCESS level could be neutral
 * while their work roles carry their job, and the acceptance and role-editing
 * paths write it today.
 *
 * The drift guard below could not catch this, because the guard's authority IS
 * the generated enum. Without this list, `isCanonicalAppRole('STAFF')` would
 * be false and a real STAFF membership would be told at runtime that their
 * role is unrecognised — the precise failure the guard was written to prevent,
 * arriving through the one door it does not watch.
 *
 * WHEN THE TYPES ARE REGENERATED this list should empty out.
 * `_PostGenerationRolesAreStillMissing` below fails the build at that moment,
 * so the shim is removed deliberately rather than left to rot.
 */
export const POST_GENERATION_APP_ROLES = Object.freeze(['STAFF'] as const);

export type PostGenerationAppRole = (typeof POST_GENERATION_APP_ROLES)[number];

/** Every role a membership can actually carry today. */
export type KnownAppRole = AppRole | PostGenerationAppRole;

/** The roles this file actually lists. */
type ListedRole = (typeof CANONICAL_APP_ROLES)[number];

/**
 * Compile-time `never` assertion.
 *
 * If the argument type is anything other than `never`, TypeScript reports
 * *"Type 'THE_ROLE' does not satisfy the constraint 'never'"* — which names
 * the offending role directly.
 */
type AssertNever<T extends never> = T;

/**
 * Every role in the GENERATED enum is listed here.
 *
 * Adding `app_role` to the database without adding it here is a compile error
 * at this line, not a person being told at runtime that their job title is
 * unrecognised.
 */
export type _EveryGeneratedRoleIsListed = AssertNever<
  Exclude<AppRole, ListedRole>
>;

/**
 * Nothing listed here is absent from the generated enum.
 *
 * Guards the other direction: a role REMOVED from the database must not go on
 * being treated as canonical, resolving to an experience nobody can hold.
 */
export type _EveryListedRoleIsGenerated = AssertNever<
  Exclude<ListedRole, AppRole>
>;

/**
 * The shim above is still needed.
 *
 * Once `types.ts` is regenerated against a schema that has `STAFF`, this
 * becomes a compile error naming the role — the signal to delete it from
 * `POST_GENERATION_APP_ROLES` and let the real guard take over again.
 */
export type _PostGenerationRolesAreStillMissing = AssertNever<
  Extract<PostGenerationAppRole, AppRole>
>;

export function isCanonicalAppRole(value: unknown): value is KnownAppRole {
  return (
    typeof value === 'string' &&
    ((CANONICAL_APP_ROLES as ReadonlyArray<string>).includes(value) ||
      (POST_GENERATION_APP_ROLES as ReadonlyArray<string>).includes(value))
  );
}

type PermissionSurface = ReturnType<typeof usePermissions>;

/**
 * The boolean capability keys that actually exist.
 *
 * Non-boolean members of the permission surface (the role string, the
 * `hasRole` helper) are excluded, so they cannot be mistaken for capabilities.
 */
export type CapabilityKey = {
  [K in keyof PermissionSurface]: PermissionSurface[K] extends boolean
    ? K
    : never;
}[keyof PermissionSurface];

// =============================================================================
// Experiences — durable identities
// =============================================================================

/**
 * The durable experience keys.
 *
 * LEGACY — retained, never routed to, meaning preserved:
 *   `technician`     Historically meant `TECHNICIAN_STEPS = []`. A row with
 *                    this key is EVIDENCE that somebody was placed in an
 *                    empty experience. Reusing it for the new six-step
 *                    technician content would rewrite the meaning of those
 *                    rows underneath them — an old `dismissed: true` would
 *                    suppress instruction that did not exist when it was
 *                    written, and an old `completed: true` would claim
 *                    somebody finished content nobody had authored.
 *   `user`           The general tour. Still explicitly startable.
 *   `setup_wizard`   CORE-W's guided setup. Not owned here.
 *
 * CURRENT — what people are routed to now:
 *   `manager`               Unchanged content and unchanged sequence, so the
 *                           existing key and its history remain correct.
 *   `technician_core_v1`    The new technician instruction.
 *   `foh_service_writer`    Front of house WITH intake and custody.
 *   `foh_dispatcher`        Front of house WITHOUT them — a different
 *                           sequence, therefore a different identity.
 *   `parts_core_v1`
 *   `readonly_core_v1`
 *   `org_overview_v1`
 */
export type OnboardingExperience =
  // legacy
  | 'technician'
  | 'user'
  | 'setup_wizard'
  // current
  | 'manager'
  | 'technician_core_v1'
  | 'foh_service_writer'
  | 'foh_dispatcher'
  | 'parts_core_v1'
  | 'readonly_core_v1'
  | 'org_overview_v1'
  // Core-100 R4 (OD-14): the fifth Core role's own walkthrough.
  | 'detail_core_v1'
  // Core-100 R4 W5 (CORE-BL-GAP-011): an owner's / manager's day-to-day operations, offered next to `manager`.
  | 'manager_ops_v1';

export const ONBOARDING_EXPERIENCES = Object.freeze([
  'technician',
  'user',
  'setup_wizard',
  'manager',
  'technician_core_v1',
  'foh_service_writer',
  'foh_dispatcher',
  'parts_core_v1',
  'readonly_core_v1',
  'org_overview_v1',
  'detail_core_v1',
  'manager_ops_v1',
] as const) satisfies ReadonlyArray<OnboardingExperience>;

/** Keys kept only so historical rows keep their meaning. */
export const LEGACY_EXPERIENCES = Object.freeze([
  'technician',
  'user',
  'setup_wizard',
] as const);

export function isLegacyExperience(
  experience: OnboardingExperience,
): boolean {
  return (LEGACY_EXPERIENCES as ReadonlyArray<string>).includes(experience);
}

/**
 * Which step library an experience draws from.
 *
 * Two experiences may share a track — `foh_service_writer` and
 * `foh_dispatcher` both read the front-of-house library — while keeping
 * separate durable identities, because applicability gives them different
 * sequences. The content is written once; only the progress is separate.
 */
export type OnboardingTrack =
  | 'manager'
  | 'user'
  | 'technician'
  | 'foh'
  | 'parts'
  | 'readonly'
  | 'org_overview'
  | 'detail'
  | 'manager_ops'
  | 'none';

export const TRACK_FOR_EXPERIENCE: Readonly<
  Record<OnboardingExperience, OnboardingTrack>
> = Object.freeze({
  manager: 'manager',
  user: 'user',
  // The legacy key keeps its legacy meaning: no content.
  technician: 'none',
  setup_wizard: 'none',
  technician_core_v1: 'technician',
  foh_service_writer: 'foh',
  foh_dispatcher: 'foh',
  parts_core_v1: 'parts',
  readonly_core_v1: 'readonly',
  org_overview_v1: 'org_overview',
  detail_core_v1: 'detail',
  manager_ops_v1: 'manager_ops',
});

/**
 * Every canonical role, mapped on purpose.
 *
 * `Record<AppRole, …>` against the GENERATED enum: adding a role to the
 * database without deciding what it is taught is a compile error here.
 *
 * Sharing is by identical sequence, not by role family:
 *   MANAGER / OWNER               share `manager` — the manager library
 *                                 carries no capability gates, so both
 *                                 receive the same ordered steps.
 *   ORG_ADMIN / REGION_MANAGER /  share `org_overview_v1`. Route authority
 *   DISTRICT_MANAGER              denies all three the shop-configuration
 *                                 pages, so none of them can be taught there.
 *   SERVICE_WRITER / DISPATCHER   do NOT share, because intake and advisor
 *                                 custody filter out for a dispatcher and
 *                                 their sequences genuinely differ.
 */
export const EXPERIENCE_FOR_ROLE: Readonly<
  Record<KnownAppRole, OnboardingExperience>
> = Object.freeze({
  // Neutral ACCESS. What a STAFF member is taught comes from their WORK
  // roles, and `resolveMultiRoleExperiences` is where that happens. This is
  // the answer for somebody who holds no work roles at all: they can see the
  // shop and nothing else, so they are shown how to read it rather than
  // walked through a job they do not have.
  STAFF: 'readonly_core_v1',
  MANAGER: 'manager',
  OWNER: 'manager',
  // NOT the manager track. `usePermissions` grants ORG_ADMIN shop
  // configuration, but `App.tsx` denies it `/shop-profile` — where almost
  // every manager lesson lives. The route is what actually gates, so the
  // manager track would have walked an org admin into a page that refuses
  // them. They can open the user guide and the org surfaces, so they get the
  // orientation track.
  ORG_ADMIN: 'org_overview_v1',
  SERVICE_WRITER: 'foh_service_writer',
  DISPATCHER: 'foh_dispatcher',
  TECHNICIAN: 'technician_core_v1',
  PARTS: 'parts_core_v1',
  AUDITOR: 'readonly_core_v1',
  REGION_MANAGER: 'org_overview_v1',
  DISTRICT_MANAGER: 'org_overview_v1',
});

export const EXPERIENCE_LABEL: Readonly<
  Record<OnboardingExperience, string>
> = Object.freeze({
  manager: 'Shop setup and management',
  technician_core_v1: 'Working a ticket',
  foh_service_writer: 'Front of house',
  foh_dispatcher: 'Scheduling and the board',
  parts_core_v1: 'Parts',
  readonly_core_v1: 'Viewing and reference',
  org_overview_v1: 'OptiBay Core orientation',
  detail_core_v1: 'Detailing work',
  manager_ops_v1: 'Running the day',
  user: 'General OptiBay tour',
  technician: 'Technician (legacy)',
  setup_wizard: 'Guided shop setup',
});

// =============================================================================
// Membership resolution
// =============================================================================

/**
 * What we know about the person's role in this shop.
 *
 * Not knowing is its own state. The previous code fell back to the technician
 * flow and called it graceful degradation — but technician is a real job, and
 * it happened to select the one empty library.
 */
export type MembershipResolution =
  | { status: 'loading' }
  | { status: 'resolved'; role: KnownAppRole }
  | { status: 'no_membership' }
  | { status: 'read_failed' }
  | { status: 'unrecognised_role'; value: string };

export type UnresolvedReason =
  | 'loading'
  | 'no_membership'
  | 'read_failed'
  | 'unrecognised_role'
  | 'no_content'
  /**
   * Content exists for this role, and the router will not let them open the
   * pages it lives on. Distinct from 'no_content', because the gap is access
   * rather than authorship — and saying "nothing is written for you" when
   * something is would misdescribe whose problem it is.
   */
  | 'route_blocked';

export type ExperienceResolution =
  | {
      status: 'experience';
      experience: OnboardingExperience;
      role: KnownAppRole;
      /**
       * Core-100 R4 CORE-BL: the person's work roles, when known. Route
       * reachability is the UNION of the legacy role and the work roles (as in
       * `ProtectedRoute`), so a STAFF detailer can be taught on the board their
       * work role opens. Absent = not known = the legacy role alone decides.
       */
      workRoles?: ReadonlyArray<string>;
    }
  | { status: 'unresolved'; reason: UnresolvedReason; detail?: string };

/**
 * What to say when there is no walkthrough.
 *
 * NONE of these promises the user guide any more. Three of them used to end
 * with *"the user guide is always available"*, and `/user-guide` is
 * role-gated — OWNER, MANAGER, SERVICE_WRITER, TECHNICIAN and ORG_ADMIN only.
 * So the messages shown to somebody with no membership, or an unreadable
 * membership, or an unrecognised role, offered a page the router may well
 * refuse them, at the exact moment they had been told there was nothing else.
 * A message about not being able to help should not end in a second dead end.
 */
export const UNRESOLVED_MESSAGE: Readonly<Record<UnresolvedReason, string>> =
  Object.freeze({
    loading: 'Checking your role in this shop…',
    no_membership:
      'You do not have an active role in this shop yet, so there is no guided walkthrough to show. You can still explore.',
    read_failed:
      'Your role in this shop could not be checked, so we cannot pick the right walkthrough. This is not a statement that there is nothing to learn — it is worth trying again shortly.',
    unrecognised_role:
      'Your role in this shop is not one this walkthrough covers yet. Rather than show you one written for somebody else, we have left it out.',
    no_content:
      'There is no guided walkthrough written for your role yet. Rather than open an empty one, we are telling you plainly.',
    route_blocked:
      'A guide exists for your role, but the pages it walks through are not ones your role can open in this shop. Rather than send you to doors that will not open, we have left it out.',
  });

export function resolveOnboardingExperience(
  membership: MembershipResolution,
): ExperienceResolution {
  switch (membership.status) {
    case 'loading':
      return { status: 'unresolved', reason: 'loading' };
    case 'no_membership':
      return { status: 'unresolved', reason: 'no_membership' };
    case 'read_failed':
      return { status: 'unresolved', reason: 'read_failed' };
    case 'unrecognised_role':
      return {
        status: 'unresolved',
        reason: 'unrecognised_role',
        detail: membership.value,
      };
    case 'resolved':
      return {
        status: 'experience',
        experience: EXPERIENCE_FOR_ROLE[membership.role],
        role: membership.role,
      };
  }
}

/**
 * Interpret whatever the membership query produced.
 *
 * A failed read, an absent row and an unfamiliar string are three different
 * facts, and this is the only place that decides which is which.
 */
export function readMembership(input: {
  isLoading: boolean;
  errored: boolean;
  role: string | null | undefined;
}): MembershipResolution {
  if (input.errored) return { status: 'read_failed' };
  if (input.isLoading) return { status: 'loading' };
  if (input.role == null || input.role === '') return { status: 'no_membership' };
  if (!isCanonicalAppRole(input.role)) {
    return { status: 'unrecognised_role', value: input.role };
  }
  return { status: 'resolved', role: input.role };
}
